import { Localization } from "@viamap/viamap2-common";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { LayerInfo } from "src/common/managers/Types";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { getTingbogUuid, isUsableTingbogSnapshot } from "src/dataCollector/parseTinglysningsDataXml";
import { PaataleberettigetResolution as PaataleberettigetResolutionView } from "src/modules/ANNModule/PaataleberettigetResolution";
import type { LLMAnalysisResultItem } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { isResolvableHistoricalPaatale } from "src/modules/ServitutRedegoerelse/model/projectAnn";
import { listServitutAktSearchLocations } from "src/modules/ANNModule/servitutAktSearch";
import { MapitStateContext } from "src/states/MapitState";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { message } from "./Message";
import { VLine } from "./MitGlassComponents";
import { TooltipWrapper } from "src/propertyInfoTemplates/PropertyComponents";
import { SFEAndColorList, type SFEColorEntry } from "./ServitutRapportData";
import type { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { ApplicationStateContext, Feature, FeatureType } from "src/states/ApplicationState";
import { buildServitutDetailsParams } from "src/modules/ANNModule/ANNServitutIdentity";
import { ServitutPaategningNotes } from "src/modules/ServitutRedegoerelse/ui/ServitutPaategningNotes";
import type { ANNServitutProvenance } from "src/modules/ANNModule/ANNServitutProvenance";
import {
   SERVITUT_ANALYSIS_FIELDS,
   analysisFieldLabel,
   formatCaseRelevantLabel,
   canResetFieldToSavedAnnotation,
   getRowFieldText,
   getSavedPaataleberettigetResolution,
   isRowFieldUntouchedAccepted,
   type ServitutAnalysisFieldKey,
   type ServitutFieldAnnotationMap,
   type ServitutFieldOverrideMap,
} from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import type { ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import {
   canUseServitutRedegoerelse,
   type ServitutRedegoerelsePhase,
} from "./ServitutRedegoerelseAccess";
import { AiRobotIcon, ServitutAiFieldChip, ServitutAktSourcePill, ServitutDigitalDocumentChip, ServitutDocumentExtentChip, ServitutImportedChip, ServitutPageFindChip, ServitutProvenanceChip, ServitutSourceHint } from "./ServitutRedegoerelseMarks";
import {
   getContentFieldAiStatus,
   getRowCopyText,
   getRowDocumentExtent,
   getRowHeading,
   getPageControlTone,
   getRowPageApprovalPlan,
   getRowPageSourceStatus,
   getRowProvenance,
   isElectronicServitutRow,
   isImportedAnnotationPendingPageSearch,
   isRowApproved,
   canUndoRowPageApproval,
   isRowFetched,
   isRowRejected,
} from "./ServitutRedegoerelseRow";
import "./ServitutRedegoerelseRapport.css";
import { ServitutMolbakHelpButton } from "./ServitutMolbakHelp.tsx";
import { servitutHelpContextFromMatrixRow } from "./servitutHelpRequest";
import { GlassMultiLayerSelect } from "./AdvGlassInput.tsx";

/** Fremdrift ved første indlæsning (tingbog → hver servitut). */
export type ServitutRedegoerelseLoadProgress = {
   stage: "registry" | "easements";
   completed: number;
   total: number;
};

/** Fremdrift ved ZIP-eksport (Excel → hvert dokument → komprimering) og PDF. */
export type ServitutRedegoerelseExportProgress = {
   completedSteps: number;
   totalSteps: number;
   stage: "spreadsheet" | "document" | "compress" | "pdf";
   activeRowKey: string | null;
   /** 1-based indeks blandt dokumenter (kun ved stage document). */
   documentIndex?: number;
   documentTotal?: number;
};

/** Legacy indskannede akt-Samlinger kræver LLM + sidevalg; digitale tingbogsdokumenter pakkes ved eksport som i ejendomsinfo (akt + SERVIMAP + bilag). */
export type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";
export type ServitutRedegoerelseDocumentSource = "tingbog" | "annotation" | "customUpload" | "rigsarkivetRequired";

export type ServitutCustomPdf = {
   s3Key: string;
   filename: string;
};

type ServitutPdfStatus = "pending" | "ready" | "missing" | "failed";
type PreparedAktSource = "korrektAkt" | "servitutAkt" | "textAkt" | "ovrigAkt" | "customAkt";

/** Per-BFE metadata for matrix headers (full matrikel list + ejerlav); ejerlav not shown inline on the column. */
export type ServitutMatrixColumnDetail = {
   matrikler: string[];
   ejerlav?: string;
};

type ServitutAktSearchHint = {
   indskannetAktDokumentId?: string | null;
};

type ServitutRedegoerelsePayload = {
   caseDescription?: string;
   aktSearchByBfe?: Record<string, ServitutAktSearchHint>;
   matrix: {
      columns: number[];
      columnLabels?: Record<string, string>;
      columnDetails?: Record<string, ServitutMatrixColumnDetail>;
      rows: {
         key: string;
         servitut?: any;
         handling?: ServitutRedegoerelseRowHandling;
         electronicServitut?: Record<string, any>;
         displayAktId: string;
         datoLoebenr: string;
         priority: string;
         summary: string;
         analysisAktId: string;
         includedBfes: number[];
         pdfStatus: ServitutPdfStatus;
         pdfSource?: PreparedAktSource;
         aktDocument?: string;
         aktId?: string;
         documentUrl?: string;
         analysisItem?: LLMAnalysisResultItem;
         selectedPages?: number[];
         exportFileName?: string;
         documentSource?: ServitutRedegoerelseDocumentSource;
         customPdf?: ServitutCustomPdf;
         fileOrigin?: boolean;
         workingDocumentLabel?: string;
         identityLabel?: string;
         annotationRecord?: ANNAnnotationRecord;
         annotationDocument?: ANNAnnotationRecord["document"];
         sourceMessage?: string;
         provenance?: ANNServitutProvenance;
         fieldAnnotations?: ServitutFieldAnnotationMap;
         fieldAiTexts?: Partial<Record<ServitutAnalysisFieldKey, string>>;
         fieldOverridePending?: ServitutFieldOverrideMap;
         fieldOverrideStamp?: number;
         stedfaestelseAnnotation?: ANNAnnotationRecord;
         job?: "idle" | "locating" | "analyzing" | "saving" | "fetching";
      }[];
   };
};

const DEFAULT_SERVITUT_BFE_COLOR = "#2563eb";

type ServitutBfeHeaderStyle = CSSProperties & {
   "--servitut-bfe-head-width": string;
   "--servitut-bfe-head-height": string;
};

const SERVITUT_B_TABS = [...SERVITUT_ANALYSIS_FIELDS, "stedfaestelse"] as const;
type ServitutBTabKey = typeof SERVITUT_B_TABS[number];
const SERVITUT_B_VISIBLE_TABS = SERVITUT_B_TABS.filter(
   (tab): tab is Exclude<ServitutBTabKey, "stedfaestelse"> => tab !== "stedfaestelse",
);

function servitutBTabLabel(tab: ServitutBTabKey): string {
   return tab === "stedfaestelse"
      ? Localization.getText("Locate on map")
      : analysisFieldLabel(tab);
}

export function ServitutRedegoerelseRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onComplete?: () => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter((layer): layer is LayerInfo => layer.propertiesInGeoJson?.includes("SFEnummer") ?? false);
   }, [mapitState.layers]);
   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((layer) => layer.layerId).includes(b)));
   }, [mapitState.layers, usableLayers]);

   const colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a]).filter(Boolean) as LayerInfo[]
      );
   }, [layerId, mapitState.layers]);

   function prepareRedegoerelse() {
      windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
         prepareColorList: colorList,
      }));
      props.onComplete?.();
   }
   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select
                     disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        props.onSelectRapport(e.currentTarget.value);
                     }}
                  >
                     {props.availableRapports.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                     ))}
                  </select>
               </GlassInputControl>
               <GlassTextInputTransformer label={Localization.getText("Property layer")} >
                  <GlassMultiLayerSelect selected={layerId} onSelect={setLayerId} options={usableLayers.map((a) => ({ label: a.datasetname, value: a.layerId }))} />
               </GlassTextInputTransformer>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => (
                     <div
                        key={a.sfe}
                        title={a.sfe + ""}
                        style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}
                     >
                        <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                     </div>
                  ))}
               </div>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButton disabled={!colorList.length} onClick={prepareRedegoerelse}>
               {Localization.getText("Generate")}
            </GlassButton>
         </ADModalFooter>
      </>
   );
}

function buildColorListForVisibleTingbog(
   propertyData: Partial<PropertyData>,
   bfeNumber: number,
): SFEColorEntry[] {
   const tingbog = propertyData.ViamapTingBog;
   return [{
      sfe: bfeNumber,
      color: DEFAULT_SERVITUT_BFE_COLOR,
      tingbogUuid: getTingbogUuid(tingbog),
      tingbog: isUsableTingbogSnapshot(tingbog) ? tingbog : undefined,
   }];
}

function getPropertyBfeFromData(propertyData: Partial<PropertyData>): number | null {
   const value =
      propertyData?.BBRRel?.bfeNummer ??
      propertyData?.BBRSFE?.[0]?.properties?.BFEnummer ??
      propertyData?.ViamapEjendomData?.sag?.BFEnr ??
      propertyData?.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer;
   const parsed = parseInt(`${value || ""}`, 10);
   return Number.isFinite(parsed) ? parsed : null;
}
export function ServitutRedegoerelseLaunchButton(props: {
   propertyData: Partial<PropertyData>;
   hasAccessToFeature: (feature: FeatureType) => boolean;
   datoLoebenrFilter?: string;
   onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
   const { dispatch: windowDispatch } = useWindowContext();
   const propertyBfe = getPropertyBfeFromData(props.propertyData);
   const canUse = canUseServitutRedegoerelse(props.hasAccessToFeature);

   if (!canUse || propertyBfe === null) {
      return null;
   }
   const bfeNumber = propertyBfe;

   function openServitutRedegoerelse(event: React.MouseEvent<HTMLElement>) {
      event.preventDefault();
      event.stopPropagation();
      windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
         prepareColorList: buildColorListForVisibleTingbog(props.propertyData, bfeNumber),
         initialPhase: "A",
         datoLoebenrFilter: props.datoLoebenrFilter,
      }));
   }

   return (
      <GlassButton
         title={Localization.getText("Open easement review for this property.")}
         onMouseDown={props.onMouseDown}
         onClick={openServitutRedegoerelse}
         style={{
            marginLeft: 0,
            minHeight: 28,
            height: 28,
            padding: "2px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
         }}
      >
         <MIBIcon icon="list" />
         <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {Localization.getText("Review easements")}
         </span>
      </GlassButton>
   );
}
function servitutExportProgressPercent(p: ServitutRedegoerelseExportProgress): number {
   if (!p.totalSteps) {
      return 0;
   }
   return Math.min(100, Math.round((p.completedSteps / p.totalSteps) * 100));
}

function servitutExportProgressCaption(p: ServitutRedegoerelseExportProgress): string {
   if (p.stage === "pdf") {
      if (p.documentIndex !== undefined && p.documentTotal !== undefined) {
         return Localization.getFormattedText("Adding easement {index} of {total}…", {
            index: p.documentIndex,
            total: p.documentTotal,
         });
      }
      return Localization.getText("Preparing PDF export…");
   }
   if (p.stage === "spreadsheet" && p.completedSteps === 0) {
      return Localization.getText("Preparing ZIP export…");
   }
   if (p.stage === "spreadsheet") {
      return Localization.getText("Adding Excel to the archive…");
   }
   if (p.stage === "compress" && p.completedSteps < p.totalSteps) {
      return Localization.getText("Compressing ZIP…");
   }
   if (p.stage === "compress") {
      return Localization.getText("Finishing…");
   }
   if (p.documentIndex !== undefined && p.documentTotal !== undefined) {
      return Localization.getFormattedText("Fetching and adding document {index} of {total}…", {
         index: p.documentIndex,
         total: p.documentTotal,
      });
   }
   return Localization.getText("Exporting…");
}

const BFE_HEAD_POPOVER_HIDE_MS = 140;

function ServitutBfeColumnHeadCell(props: {
   bfe: number;
   shortLabel: string;
   popover: ServitutMatrixColumnDetail;
   ariaLabel: string;
}) {
   const wrapRef = useRef<HTMLDivElement | null>(null);
   const innerRef = useRef<HTMLSpanElement | null>(null);
   const [labelBox, setLabelBox] = useState<{ w: number; h: number } | null>(null);
   const [open, setOpen] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const measureLabel = useCallback(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const r = inner.getBoundingClientRect();
      /** Axis-aligned bounds of the rotated label — drives column width and header row height. */
      const horizontalPad = 12;
      const bottomPad = 12;
      setLabelBox({
         w: Math.ceil(r.width) + horizontalPad,
         h: Math.ceil(r.height) + bottomPad,
      });
   }, []);

   useLayoutEffect(() => {
      measureLabel();
   }, [measureLabel, props.shortLabel]);

   useEffect(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const ro = new ResizeObserver(() => measureLabel());
      ro.observe(inner);
      return () => ro.disconnect();
   }, [measureLabel]);

   useEffect(() => {
      const onResize = () => measureLabel();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
   }, [measureLabel]);

   useEffect(() => {
      const fonts = document.fonts;
      if (!fonts?.ready) return;
      let cancelled = false;
      fonts.ready.then(() => {
         if (!cancelled) measureLabel();
      });
      return () => {
         cancelled = true;
      };
   }, [measureLabel]);

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 8;
      setPos({ top: r.bottom + margin, left: r.left });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const scheduleClose = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => setOpen(false), BFE_HEAD_POPOVER_HIDE_MS);
   };

   useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
   }, [open, props.shortLabel]);

   useEffect(() => {
      if (!open) return;
      const close = () => setOpen(false);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
      return () => {
         window.removeEventListener("scroll", close, true);
         window.removeEventListener("resize", close);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   const popoverContent = (
      <div
         className="servitut-redegoerelse__bfePopover"
         style={{ top: pos.top, left: pos.left }}
         onMouseEnter={clearHideTimer}
         onMouseLeave={scheduleClose}
         role="tooltip"
      >
         <div className="servitut-redegoerelse__bfePopoverBfe">
            {Localization.getFormattedText("BFE {bfe}", { bfe: props.bfe })}
         </div>
         {props.popover.ejerlav ? (
            <div className="servitut-redegoerelse__bfePopoverEjerlav">
               <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral district (ejerlav)")}</span>
               {props.popover.ejerlav}
            </div>
         ) : null}
         <div className="servitut-redegoerelse__bfePopoverMatrikelBlock">
            <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral plots")}</span>
            <ul className="servitut-redegoerelse__bfePopoverList">
               {props.popover.matrikler.map((m, index) => (
                  <li key={`${props.bfe}-${index}-${m}`}>{m}</li>
               ))}
            </ul>
         </div>
      </div>
   );

   const thStyle: ServitutBfeHeaderStyle | undefined = labelBox
      ? ({
         "--servitut-bfe-head-width": `${labelBox.w}px`,
         "--servitut-bfe-head-height": `${labelBox.h}px`,
      } as ServitutBfeHeaderStyle)
      : undefined;

   return (
      <th
         className="servitut-redegoerelse__bfeHead"
         style={thStyle}
         aria-label={props.ariaLabel}
      >
         <div
            ref={wrapRef}
            className="servitut-redegoerelse__bfeHeadTrigger"
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
         >
            <span ref={innerRef} className="servitut-redegoerelse__bfeHeadInner">
               <span className="servitut-redegoerelse__bfeHeadMatrikel">{props.shortLabel}</span>
            </span>
         </div>
         {open ? createPortal(popoverContent, document.body) : null}
      </th>
   );
}

export function ServitutMatrix(props: {
   payload: ServitutRedegoerelsePayload;
   analyzing: boolean;
   aiJobKeys: Set<string>;
   analysisProgress: { completed: number; total: number } | null;
   openingKey: string | null;
   exporting: boolean;
   exportProgress: ServitutRedegoerelseExportProgress | null;
   savingAnnotationKeys: Set<string>;
   justSavedKeys: Set<string>;
   unsavedChangeKeys: Set<string>;
   resolvingPaataleKeys: Set<string>;
   analysisField: ServitutAnalysisFieldKey;
   onAnalysisFieldChange: (next: ServitutAnalysisFieldKey) => void;
   onOpenRow: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onAcceptField: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number], text?: string) => void | Promise<void>;
   onUndoField: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onResetField?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onAcceptAllField: (entries: Array<{ row: ServitutRedegoerelsePayload["matrix"]["rows"][number]; text?: string }>) => void;
   onUndoAllField?: (rows: Array<ServitutRedegoerelsePayload["matrix"]["rows"][number]>) => void;
   onResolvePaataleberettiget: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onUploadCustomPdf: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number], file: File) => void;
   onSelectAkt?: (
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      aktId: string,
      source: PreparedAktSource,
   ) => void;
   onFetchBilag?: (
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      bilagId: string,
   ) => void;
   onAnalyze: () => void;
   onStopAnalysis: () => void;
   onStopAktFetch?: () => void;
   onRunRowAi?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   canAnalyze: boolean;
   aktFetchProgress?: { completed: number; total: number } | null;
   caseDescription: string;
   onCaseDescriptionChange: (value: string) => void;
   onExport: (mode: "excel" | "zip" | "pdf" | "bundle") => void;
   phase?: ServitutRedegoerelsePhase;
   onPhaseChange?: (phase: ServitutRedegoerelsePhase) => void;
   canSeeA?: boolean;
   canSeeB?: boolean;
   canWriteA?: boolean;
   canWriteB?: boolean;
   incompleteACount?: number;
   rejectedACount?: number;
   onCompareFolder?: () => void;
   onApprovePages?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onRejectPages?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onRevertPages?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onApproveAllPages?: (rows: ServitutRedegoerelsePayload["matrix"]["rows"]) => void;
   onRevertAllPages?: (rows: ServitutRedegoerelsePayload["matrix"]["rows"]) => void;
   onOpenStedfaestelse?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
}) {
   const phase = props.phase || "A";
   const showAnalysisColumns = phase === "B";
   const uploadInputRef = useRef<HTMLInputElement | null>(null);
   const uploadRowRef = useRef<ServitutRedegoerelsePayload["matrix"]["rows"][number] | null>(null);
   const exportMenuRef = useRef<HTMLDetailsElement | null>(null);
   const [query, setQuery] = useState("");
   const [statusFilter, setStatusFilter] = useState<"all" | "found" | "uncertain" | "missing" | "treated" | "untreated">("all");
   const [matrixSort, setMatrixSort] = useState<ServitutMatrixSortState>(null);
   const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
   const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
   const [bTab, setBTab] = useState<ServitutBTabKey>(props.analysisField);
   const [paataleNoticeKey, setPaataleNoticeKey] = useState<string | null>(null);
   const isStedfaestelseTab = bTab === "stedfaestelse";
   const selectBTab = (tab: ServitutBTabKey) => {
      setBTab(tab);
      if (tab !== "stedfaestelse") {
         props.onAnalysisFieldChange(tab);
      }
   };
   const fieldDraftKey = (rowKey: string) => `${rowKey}:${props.analysisField}`;
   const hasFieldDraft = (rowKey: string) => Object.prototype.hasOwnProperty.call(fieldDrafts, fieldDraftKey(rowKey));
   const displayedFieldText = (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => (
      hasFieldDraft(row.key)
         ? fieldDrafts[fieldDraftKey(row.key)]
         : getRowFieldText(row, props.analysisField, props.caseDescription, {
            preferLiveAi: Boolean(row.fieldOverridePending?.[props.analysisField]),
         })
   );
   const clearFieldDraft = (rowKey: string) => {
      setFieldDrafts((prev) => {
         const key = fieldDraftKey(rowKey);
         if (!(key in prev)) {
            return prev;
         }
         const copy = { ...prev };
         delete copy[key];
         return copy;
      });
   };
   const stopEditingField = (rowKey?: string) => {
      setEditingFieldKey((current) => (
         !rowKey || current === fieldDraftKey(rowKey) ? null : current
      ));
   };
   useEffect(() => {
      setEditingFieldKey(null);
   }, [props.analysisField]);
   const previousOverrideStampsRef = useRef<Record<string, number>>({});
   useEffect(() => {
      const nextStamps: Record<string, number> = {};
      const changedRows: Array<ServitutRedegoerelsePayload["matrix"]["rows"][number]> = [];
      props.payload.matrix.rows.forEach((row) => {
         const stamp = row.fieldOverrideStamp ?? 0;
         nextStamps[row.key] = stamp;
         if (previousOverrideStampsRef.current[row.key] !== undefined && previousOverrideStampsRef.current[row.key] !== stamp) {
            changedRows.push(row);
         }
      });
      previousOverrideStampsRef.current = nextStamps;
      if (!changedRows.length) {
         return;
      }
      setFieldDrafts((prev) => {
         const copy = { ...prev };
         let changed = false;
         changedRows.forEach((row) => {
            SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
               if (!row.fieldOverridePending?.[field]) {
                  return;
               }
               const key = `${row.key}:${field}`;
               if (key in copy) {
                  delete copy[key];
                  changed = true;
               }
            });
         });
         return changed ? copy : prev;
      });
   }, [props.payload.matrix.rows]);
   const normalizedQuery = query.trim().toLowerCase();
   // Prefer the live editor value so a case typed after import (before full re-run) updates the pill.
   const hasCaseDescription = Boolean(props.caseDescription.trim() || props.payload.caseDescription?.trim());
   const filteredRows = useMemo(() => {
      const rows = props.payload.matrix.rows.filter((row) => {
         const handling = row.handling ?? "legacyLlmPdf";
         const analysisSearchText = [
            row.analysisItem?.servitut_type,
            row.analysisItem?.paataleberettiget,
            row.analysisItem?.paataleberettiget_resolution?.suggested_current_name,
            row.analysisItem?.paataleberettiget_resolution?.reasoning,
            ...(row.analysisItem?.paataleberettiget_resolution?.alternatives || []).map((alternative) => alternative.name),
            row.analysisItem?.case_description,
            row.analysisItem?.case_relevance_reasoning,
            row.analysisItem?.overall_confidence_reasoning,
            ...(row.analysisItem?.review_reasons || []),
            formatCaseRelevantLabel(row.analysisItem?.case_relevant),
         ].filter(Boolean).join(" ").toLowerCase();
         const matchesQuery = !normalizedQuery ||
            row.datoLoebenr.toLowerCase().includes(normalizedQuery) ||
            row.displayAktId.toLowerCase().includes(normalizedQuery) ||
            row.summary.toLowerCase().includes(normalizedQuery) ||
            analysisSearchText.includes(normalizedQuery) ||
            row.includedBfes.some((bfe) => `${bfe}`.includes(normalizedQuery));
         if (!matchesQuery) return false;
         if (phase === "B" && !isRowApproved(row)) return false;
         if (statusFilter === "found") {
            const source = getRowPageSourceStatus(row);
            return (source.kind === "ai_pages" && source.confident)
               || (handling === "electronicZipPackage" && row.pdfStatus === "ready");
         }
         if (statusFilter === "uncertain") {
            const source = getRowPageSourceStatus(row);
            return source.kind === "ai_pages" && !source.confident;
         }
         if (statusFilter === "missing") {
            return handling === "legacyLlmPdf"
               && Boolean(row.analysisItem)
               && !(row.analysisItem?.pages_kept?.length);
         }
         if (statusFilter === "treated") {
            return phase === "B"
               ? isRowFieldUntouchedAccepted(row, props.analysisField, hasFieldDraft(row.key))
               : isRowApproved(row) || isRowRejected(row);
         }
         if (statusFilter === "untreated") {
            return phase === "B"
               ? !isRowFieldUntouchedAccepted(row, props.analysisField, hasFieldDraft(row.key))
               : !isRowApproved(row) && !isRowRejected(row);
         }
         return true;
      });
      return applyServitutMatrixSort(rows, props.payload.matrix.columns, hasCaseDescription, matrixSort);
   }, [fieldDrafts, hasCaseDescription, matrixSort, normalizedQuery, phase, props.analysisField, props.caseDescription, props.payload.caseDescription, props.payload.matrix.columns, props.payload.matrix.rows, statusFilter]);
   const contentAnalyzedCount = props.payload.matrix.rows.filter((row) => hasContentAnalysisFields(row.analysisItem)).length;
   const exportCount = props.payload.matrix.rows.length;
   const fieldAcceptableRows = filteredRows.filter((row) => (
      displayedFieldText(row).trim()
      && !isRowFieldUntouchedAccepted(row, props.analysisField, hasFieldDraft(row.key))
      && !isServitutRowAwaitingAi(row, props.aiJobKeys)
   ));
   const pageApprovableRows = filteredRows.filter((row) => (
      !isRowApproved(row)
      && !isRowRejected(row)
      && getRowPageApprovalPlan(row).canApprove
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, props.aiJobKeys)
   ));
   const pageRevertableRows = filteredRows.filter((row) => (
      canUndoRowPageApproval(row)
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, props.aiJobKeys)
   ));
   const fieldUndoableRows = filteredRows.filter((row) => (
      isRowFieldUntouchedAccepted(row, props.analysisField, hasFieldDraft(row.key))
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, props.aiJobKeys)
   ));
   const cycleMatrixSort = (column: ServitutMatrixSortColumn) => {
      setMatrixSort((prev) => {
         if (!prev || prev.column !== column) {
            const direction = column === "confidence" ? "desc" : "asc";
            return { column, direction };
         }
         if (column === "confidence") {
            if (prev.direction === "desc") return { column, direction: "asc" };
            return null;
         }
         if (prev.direction === "asc") return { column, direction: "desc" };
         return null;
      });
   };
   const matrixSortIndicator = (column: ServitutMatrixSortColumn) => {
      if (!matrixSort || matrixSort.column !== column) return "↕";
      return matrixSort.direction === "asc" ? "↑" : "↓";
   };
   const copyTextToClipboard = async (text: string) => {
      const value = text.trim();
      if (!value) {
         message.error(Localization.getText("Nothing to copy"));
         return;
      }
      try {
         await navigator.clipboard.writeText(value);
         message.info(Localization.getText("Copied to clipboard"));
      } catch (error) {
         console.error("[Servitutredegørelse] clipboard copy failed", error);
         message.error(Localization.getText("Failed to copy to clipboard"));
      }
   };

   const exportProgressPct = props.exportProgress
      ? servitutExportProgressPercent(props.exportProgress)
      : 0;
   const exportProgressLabel = props.exportProgress
      ? servitutExportProgressCaption(props.exportProgress)
      : "";
   const paataleNoticeRow = paataleNoticeKey
      ? props.payload.matrix.rows.find((row) => row.key === paataleNoticeKey)
      : undefined;
   const paataleNoticeResolution = paataleNoticeRow
      ? getRowPaataleberettigetResolution(paataleNoticeRow)
      : undefined;

   return (
      <div className="servitut-redegoerelse">
         <input
            ref={uploadInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="servitut-redegoerelse__uploadInput"
            onChange={(event) => {
               const file = event.currentTarget.files?.[0];
               const row = uploadRowRef.current;
               event.currentTarget.value = "";
               uploadRowRef.current = null;
               if (file && row) {
                  props.onUploadCustomPdf(row, file);
               }
            }}
         />
         <div className="servitut-redegoerelse__hero">
            <div>
               <div className="servitut-redegoerelse__eyebrow">{Localization.getText("Easement review")}</div>
               {props.canSeeB !== false ? (
                  <div className="servitut-redegoerelse__phaseTabs" role="tablist">
                     {props.canSeeA !== false ? (
                        <button
                           type="button"
                           role="tab"
                           aria-selected={phase === "A"}
                           className={`servitut-redegoerelse__phaseTab ${phase === "A" ? "servitut-redegoerelse__phaseTab--active" : ""}`}
                           onClick={() => props.onPhaseChange?.("A")}
                        >
                           {Localization.getText("A: Find and control")}
                        </button>
                     ) : null}
                     <button
                        type="button"
                        role="tab"
                        aria-selected={phase === "B"}
                        className={`servitut-redegoerelse__phaseTab ${phase === "B" ? "servitut-redegoerelse__phaseTab--active" : ""}`}
                        onClick={() => props.onPhaseChange?.("B")}
                     >
                        {Localization.getText("B: Analyse")}
                     </button>
                  </div>
               ) : null}
               <h2>{phase === "B" ? Localization.getText("Analyse easement content") : Localization.getText("Find and control easements")}</h2>
               <p>
                  {phase === "B"
                     ? Localization.getText("Rewrite a field if AI is wrong or missing. That text is saved as the only annotation of that kind.")
                     : Localization.getText("Fetch or upload documents, check that AI found the pages, then approve the page selection.")}
               </p>
               {phase === "A" ? (
                  <button
                     type="button"
                     className="servitut-redegoerelse__folderCompare"
                     onClick={props.onCompareFolder}
                  >
                     {Localization.getText("Compare with folder")}
                  </button>
               ) : null}
               {phase === "B" && ((props.incompleteACount || 0) > 0 || (props.rejectedACount || 0) > 0) ? (
                  <div className="servitut-redegoerelse__incompleteBanner" role="status">
                     {(props.incompleteACount || 0) > 0 ? (
                        <div>
                           {Localization.getFormattedText("{count} easements are not fetched or not controlled.", { count: props.incompleteACount || 0 })}
                        </div>
                     ) : null}
                     {(props.rejectedACount || 0) > 0 ? (
                        <div>
                           {Localization.getFormattedText("{count} easements were ignored and are not being analysed.", { count: props.rejectedACount || 0 })}
                        </div>
                     ) : null}
                  </div>
               ) : null}
            </div>
            <div className={`servitut-redegoerelse__aiPanel servitut-redegoerelse__aiPanel--hero${phase === "B" ? " servitut-redegoerelse__aiPanel--withCase" : ""}`}>
               {phase === "B" ? (
                  <label className="servitut-redegoerelse__casePanel">
                     <span className="servitut-redegoerelse__caseLabel">
                        {Localization.getText("Problemstilling")}
                        <TooltipWrapper
                           title={Localization.getText("Write a problem statement. AI will assess whether the easement is relevant to it.")}
                           className="ViewButtonToolTip GlassLabelInfo"
                        >
                           <span
                              className="servitut-redegoerelse__caseHelp"
                              aria-label={Localization.getText("Write a problem statement. AI will assess whether the easement is relevant to it.")}
                           >
                              <MIBIcon icon="info" style={{ fontSize: 13 }} />
                           </span>
                        </TooltipWrapper>
                     </span>
                     <textarea
                        className="servitut-redegoerelse__caseInput"
                        value={props.caseDescription}
                        onChange={(e) => props.onCaseDescriptionChange(e.currentTarget.value)}
                        placeholder={Localization.getText("Optional: describe the problemstilling that AI should assess for relevance.")}
                        rows={3}
                     />
                  </label>
               ) : null}
               <button
                  key={phase === "A" ? "ai-start-A" : "ai-start-B"}
                  className={`servitut-redegoerelse__aiStart servitut-redegoerelse__aiStart--hero ${phase === "A" && props.aktFetchProgress
                     ? "servitut-redegoerelse__aiStart--fetching"
                     : props.analyzing
                        ? "servitut-redegoerelse__aiStart--stop"
                        : ""
                     }`}
                  disabled={
                     phase === "A" && props.aktFetchProgress
                        ? false
                        : !props.analyzing && (props.exporting || !props.canAnalyze || (phase === "A" ? props.canWriteA === false : props.canWriteB === false))
                  }
                  title={
                     phase === "A" && props.aktFetchProgress
                        ? Localization.getText("Press to stop collecting akts")
                        : undefined
                  }
                  onClick={
                     phase === "A" && props.aktFetchProgress
                        ? props.onStopAktFetch
                        : props.analyzing
                           ? props.onStopAnalysis
                           : props.onAnalyze
                  }
               >
                  {phase === "A" && props.aktFetchProgress ? (
                     <>
                        <span
                           className="servitut-redegoerelse__aiStartFill"
                           style={{
                              width: `${Math.round((props.aktFetchProgress.completed / Math.max(props.aktFetchProgress.total, 1)) * 100)}%`,
                           }}
                        />
                        <Spinner size="sm" />
                        <span className="servitut-redegoerelse__aiStartCopy">
                           <span>
                              {Localization.getFormattedText("Fetching akts {completed}/{total}", {
                                 completed: props.aktFetchProgress.completed,
                                 total: props.aktFetchProgress.total,
                              })}
                           </span>
                           <span className="servitut-redegoerelse__aiStartHint">
                              {Localization.getText("Press to stop collecting akts")}
                           </span>
                        </span>
                     </>
                  ) : props.analyzing ? (
                     <>
                        <AiRobotIcon />
                        <Spinner size="sm" />
                        <span>
                           {phase === "A"
                              ? Localization.getText("Stop page finding")
                              : Localization.getText("Stop content analysis")}
                        </span>
                     </>
                  ) : (
                     <>
                        <AiRobotIcon />
                        <span>
                           {phase === "A"
                              ? Localization.getText("Start page finding with AI")
                              : (contentAnalyzedCount
                                 ? Localization.getText("Restart content analysis with AI")
                                 : Localization.getText("Start content analysis with AI"))}
                        </span>
                     </>
                  )}
               </button>
               {props.analyzing && props.analysisProgress && props.analysisProgress.total > 0 ? (
                  <div
                     className="servitut-redegoerelse__aiProgress"
                     role="progressbar"
                     aria-valuemin={0}
                     aria-valuemax={props.analysisProgress.total}
                     aria-valuenow={props.analysisProgress.completed}
                     aria-label={Localization.getText("AI analysis progress")}
                  >
                     <div className="servitut-redegoerelse__aiProgressTrack">
                        <div
                           className="servitut-redegoerelse__aiProgressFill"
                           style={{
                              width: `${Math.round((props.analysisProgress.completed / props.analysisProgress.total) * 100)}%`,
                           }}
                        />
                     </div>
                     <span className="servitut-redegoerelse__aiProgressCount">
                        {props.analysisProgress.completed}/{props.analysisProgress.total}
                     </span>
                  </div>
               ) : null}
            </div>
         </div>

         <div className="servitut-redegoerelse__toolbar">
            <div className="servitut-redegoerelse__toolbarRow">
               <input
                  className="servitut-redegoerelse__search"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  placeholder={Localization.getText("Search akt, text or BFE…")}
               />
               <select
                  className="servitut-redegoerelse__filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.currentTarget.value as typeof statusFilter)}
               >
                  <option value="all">{Localization.getText("All rows")}</option>
                  <option value="found">{Localization.getText("AI found pages")}</option>
                  <option value="uncertain">{Localization.getText("AI found pages, not confident")}</option>
                  <option value="missing">{Localization.getText("AI found no pages")}</option>
                  <option value="treated">{phase === "B" ? Localization.getText("Accepted") : Localization.getText("Approved")}</option>
                  <option value="untreated">{phase === "B" ? Localization.getText("Not accepted") : Localization.getText("Not treated")}</option>
               </select>
               {props.exporting || exportCount === 0 ? (
                  <button
                     type="button"
                     className="servitut-redegoerelse__export"
                     disabled
                     aria-label={Localization.getFormattedText("Export ({count})", { count: exportCount })}
                  >
                     {props.exporting
                        ? Localization.getText("Exporting...")
                        : Localization.getFormattedText("Export ({count})", { count: exportCount })}
                  </button>
               ) : (
                  <details ref={exportMenuRef} className="servitut-redegoerelse__exportDetails">
                     <summary
                        className="servitut-redegoerelse__export"
                        aria-label={Localization.getFormattedText("Export ({count})", { count: exportCount })}
                     >
                        {Localization.getFormattedText("Export ({count})", { count: exportCount })}
                     </summary>
                     <div className="servitut-redegoerelse__exportOptions" role="menu">
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("excel");
                           }}
                        >
                           {Localization.getText("Export Excel")}
                        </button>
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("pdf");
                           }}
                        >
                           {Localization.getText("Export PDF")}
                        </button>
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("zip");
                           }}
                        >
                           {Localization.getText("Export easements and Excel")}
                        </button>
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("bundle");
                           }}
                        >
                           {Localization.getText("Export one PDF with contents")}
                        </button>
                     </div>
                  </details>
               )}
            </div>
            {props.exporting && props.exportProgress ? (
               <div className="servitut-redegoerelse__exportProgress" aria-live="polite">
                  <div className="servitut-redegoerelse__exportProgressTrack">
                     <div
                        className="servitut-redegoerelse__exportProgressFill"
                        style={{ width: `${exportProgressPct}%` }}
                     />
                  </div>
                  <div className="servitut-redegoerelse__exportProgressFooter">
                     <Spinner animation="border" size="sm" className="servitut-redegoerelse__exportProgressSpinner" />
                     <span className="servitut-redegoerelse__exportProgressText">
                        {exportProgressPct}% — {exportProgressLabel}
                     </span>
                  </div>
               </div>
            ) : null}
         </div>

         {showAnalysisColumns ? (
            <div className="servitut-redegoerelse__fieldTabs" role="tablist" aria-label={Localization.getText("Analysis field")}>
               {SERVITUT_B_VISIBLE_TABS.map((tab) => (
                  <button
                     key={tab}
                     type="button"
                     role="tab"
                     aria-selected={bTab === tab}
                     className={`servitut-redegoerelse__fieldTab ${bTab === tab ? "servitut-redegoerelse__fieldTab--active" : ""}`}
                     onClick={() => selectBTab(tab)}
                  >
                     {servitutBTabLabel(tab)}
                  </button>
               ))}
            </div>
         ) : null}

         <div className="servitut-redegoerelse__tableWrap">
            <table className={`servitut-redegoerelse__table ${phase === "B" ? "servitut-redegoerelse__table--phaseB" : "servitut-redegoerelse__table--phaseA"}`}>
               <thead>
                  <tr>
                     <th className="servitut-redegoerelse__priorityHead">
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("priority")}
                           title={Localization.getText("Sort by priority. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Priority")}</span>
                           <span>{matrixSortIndicator("priority")}</span>
                        </button>
                     </th>
                     <th className="servitut-redegoerelse__datoHead">
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("dato")}
                           title={Localization.getText("Sort by date / serial no. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Date / serial no.")}</span>
                           <span>{matrixSortIndicator("dato")}</span>
                        </button>
                     </th>
                     <th className="servitut-redegoerelse__savedHead">{Localization.getText("Status")}</th>
                     {showAnalysisColumns ? (
                        <th className="servitut-redegoerelse__fieldHead servitut-redegoerelse__focusCol">
                           {servitutBTabLabel(bTab)}
                        </th>
                     ) : null}
                     {phase !== "B" ? (
                        <th className="servitut-redegoerelse__actionsHead">
                           <div className="servitut-redegoerelse__actionsHeadInner">
                              <span>{Localization.getText("Actions")}</span>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--approve"
                                 disabled={pageApprovableRows.length === 0 || props.exporting || props.canWriteA === false}
                                 title={
                                    pageApprovableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Approve all")
                                 }
                                 onClick={() => props.onApproveAllPages?.(pageApprovableRows)}
                              >
                                 {Localization.getText("Approve all")}
                              </button>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                 disabled={pageRevertableRows.length === 0 || props.exporting || props.canWriteA === false}
                                 title={
                                    pageRevertableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Undo all")
                                 }
                                 onClick={() => props.onRevertAllPages?.(pageRevertableRows)}
                              >
                                 {Localization.getText("Undo all")}
                              </button>
                           </div>
                        </th>
                     ) : null}
                     {showAnalysisColumns && !isStedfaestelseTab ? (
                        <th className="servitut-redegoerelse__actionsHead">
                           <div className="servitut-redegoerelse__actionsHeadInner">
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--accept"
                                 disabled={fieldAcceptableRows.length === 0 || props.exporting || props.canWriteB === false}
                                 title={
                                    fieldAcceptableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Accept all of this field")
                                 }
                                 onClick={() => {
                                    setEditingFieldKey(null);
                                    props.onAcceptAllField(
                                       fieldAcceptableRows.map((row) => ({ row, text: displayedFieldText(row) })),
                                    );
                                 }}
                              >
                                 {Localization.getText("Accept all")}
                              </button>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                 disabled={fieldUndoableRows.length === 0 || props.exporting || props.canWriteB === false}
                                 title={
                                    fieldUndoableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Undo all of this field")
                                 }
                                 onClick={() => {
                                    setEditingFieldKey(null);
                                    props.onUndoAllField?.(fieldUndoableRows);
                                 }}
                              >
                                 {Localization.getText("Undo all")}
                              </button>
                           </div>
                        </th>
                     ) : null}
                     {props.payload.matrix.columns.map((bfe) => {
                        const label = getServitutMatrixColumnLabel(props.payload, bfe);
                        const shortHeaderText = getServitutMatrixColumnHeaderShort(props.payload, bfe);
                        const popoverDetail = getServitutMatrixColumnPopoverDetail(props.payload, bfe);
                        const columnAriaLabel = Localization.getFormattedText("{label} (BFE {bfe})", { label, bfe });
                        return (
                           <ServitutBfeColumnHeadCell
                              key={bfe}
                              bfe={bfe}
                              shortLabel={shortHeaderText}
                              popover={popoverDetail}
                              ariaLabel={columnAriaLabel}
                           />
                        );
                     })}
                  </tr>
               </thead>
               <tbody>
                  {filteredRows.map((row) => {
                     const isResolvingPaatale = props.resolvingPaataleKeys.has(row.key);
                     const isSavingAnnotation = props.savingAnnotationKeys.has(row.key);
                     const wasJustSaved = props.justSavedKeys.has(row.key);
                     const hasUnsavedChange = props.unsavedChangeKeys.has(row.key);
                     const actionStatus = getRowActionStatus({
                        hasAnalysis: Boolean(row.analysisItem),
                        isSaving: isSavingAnnotation,
                        wasJustSaved,
                        hasUnsavedChange,
                        hasSavedAnalysis: Boolean(row.annotationRecord?.document?.llmAnalysis),
                     });
                     const awaitingAi = isServitutRowAwaitingAi(row, props.aiJobKeys);
                     const awaitingAkt = isRowAwaitingAkt(row);
                     const rowIsBeingAnalyzed = awaitingAi;
                     const rowIsOpening = props.openingKey === row.key;
                     const rowOpensViewer = row.documentSource === "rigsarkivetRequired"
                        ? false
                        : Boolean(
                           row.customPdf?.s3Key
                           || row.electronicServitut
                           || row.documentSource === "annotation"
                           || row.documentSource === "customUpload"
                           || row.pdfStatus === "ready",
                        );
                     const rowIsExportFocus = Boolean(
                        props.exporting
                        && props.exportProgress?.stage === "document"
                        && props.exportProgress.activeRowKey === row.key,
                     );
                     const rowIsBusy = rowIsBeingAnalyzed
                        || awaitingAkt
                        || rowIsOpening
                        || isResolvingPaatale
                        || isSavingAnnotation
                        || rowIsExportFocus;
                     const fieldUntouchedAccepted = isRowFieldUntouchedAccepted(
                        row,
                        props.analysisField,
                        hasFieldDraft(row.key),
                     );
                     const fieldText = displayedFieldText(row);
                     const fieldStatus = fieldUntouchedAccepted
                        ? "person_approved" as const
                        : getContentFieldAiStatus(row, props.analysisField, { hasCaseDescription });
                     const pageTone = getPageControlTone(row);
                     const datoLoebenrCopyText = getRowCopyText(row);
                     const headingText = getRowHeading(row);
                     const annotationHint = (
                        <ServitutSourceHint
                           mark="A"
                           tone="annotation"
                           text={row.sourceMessage || Localization.getText("Using saved annotation PDF")}
                        />
                     );
                     const fieldAnnotationHint = (
                        <ServitutSourceHint
                           mark="A"
                           tone="annotation"
                           text={Localization.getText("Field annotation saved")}
                        />
                     );
                     const sourceHint = phase === "B"
                        ? (isStedfaestelseTab
                           ? (row.stedfaestelseAnnotation ? fieldAnnotationHint : null)
                           : (fieldUntouchedAccepted ? fieldAnnotationHint : null))
                        : isRowApproved(row)
                           ? annotationHint
                           : row.documentSource === "annotation" ? annotationHint
                              : row.documentSource === "rigsarkivetRequired" ? (
                                 <ServitutSourceHint
                                    mark="R"
                                    tone="rigsarkiv"
                                    text={row.sourceMessage || Localization.getText("Rigsarkivet")}
                                 />
                              ) : row.sourceMessage && (row.pdfStatus === "missing" || row.pdfStatus === "failed") ? (
                                 <ServitutSourceHint
                                    mark="?"
                                    tone="missing"
                                    text={row.sourceMessage}
                                 />
                              ) : null;
                     const isElectronicRow = (row.handling ?? "legacyLlmPdf") === "electronicZipPackage";
                     const aktSourceLocations = isElectronicRow
                        ? []
                        : listServitutAktSearchLocations(
                           row.servitut,
                           stamOplysningerHintForRow(props.payload, row),
                        );
                     const pageSource = getRowPageSourceStatus(row);
                     const documentExtent = getRowDocumentExtent(row);
                     const pageSourceForChip = isRowApproved(row)
                        && (pageSource.kind === "ai_none"
                           || (pageSource.kind === "ai_pages" && !pageSource.confident))
                        ? { kind: "hidden" as const }
                        : pageSource;
                     const viewControl = rowIsOpening ? (
                        <span className="servitut-redegoerelse__viewButton servitut-redegoerelse__viewButton--busy" title={Localization.getText("Loading...")}>
                           <Spinner
                              animation="border"
                              role="status"
                              size="sm"
                              className="servitut-redegoerelse__rowBusySpinner"
                           />
                        </span>
                     ) : (
                        <button
                           type="button"
                           className="servitut-redegoerelse__viewButton"
                           disabled={!rowOpensViewer}
                           onClick={() => {
                              if (rowOpensViewer) {
                                 props.onOpenRow(row);
                              }
                           }}
                           title={rowOpensViewer
                              ? Localization.getText("View document")
                              : Localization.getText("No PDF available for this document")}
                           aria-label={rowOpensViewer
                              ? Localization.getText("View document")
                              : Localization.getText("No PDF available for this document")}
                        >
                           <MIBIcon icon="pdf" style={{ fontSize: 14 }} />
                           <span>{Localization.getText("View document")}</span>
                        </button>
                     );
                     return (
                        <tr
                           key={row.key}
                           className={`servitut-redegoerelse__row ${phase === "A"
                              ? `servitut-redegoerelse__row--page-${pageTone}`
                              : `servitut-redegoerelse__row--field-${(isStedfaestelseTab ? Boolean(row.stedfaestelseAnnotation) : fieldUntouchedAccepted)
                                 ? "accepted"
                                 : "pending"
                              }`
                              }${rowIsExportFocus ? " servitut-redegoerelse__row--exportActive" : ""}${rowIsBusy ? " servitut-redegoerelse__row--busy" : ""}`}
                        >
                           <td className="servitut-redegoerelse__priorityCell"><span className="servitut-redegoerelse__priority">{row.priority || "-"}</span></td>
                           <td className="servitut-redegoerelse__aktCell">
                              <div className="servitut-redegoerelse__aktCellInner">
                                 <div className="servitut-redegoerelse__aktMain">
                                    <div className="servitut-redegoerelse__aktIdLine">
                                       <ServitutDatoLoebenrLink row={row} />
                                       {isElectronicRow ? null : (
                                          <ServitutAktSourcePill
                                             aktId={row.fileOrigin ? undefined : row.aktId}
                                             usedSource={row.fileOrigin ? undefined : row.pdfSource}
                                             locations={aktSourceLocations}
                                             fileLabel={row.fileOrigin
                                                ? (row.workingDocumentLabel || row.customPdf?.filename)
                                                : undefined}
                                             disabled={props.exporting || awaitingAi || awaitingAkt || isSavingAnnotation || props.canWriteA === false}
                                             busy={isSavingAnnotation || awaitingAkt}
                                             onSelectLocation={(location) => {
                                                if (!location.aktId) {
                                                   return;
                                                }
                                                props.onSelectAkt?.(row, location.aktId, location.source);
                                             }}
                                             onFetchAkt={(aktId) => {
                                                const match = aktSourceLocations.find((location) => location.aktId === aktId);
                                                props.onSelectAkt?.(row, aktId, match?.source || "customAkt");
                                             }}
                                             onFetchBilag={props.onFetchBilag
                                                ? (bilagId) => props.onFetchBilag?.(row, bilagId)
                                                : undefined}
                                          />
                                       )}
                                       {sourceHint}
                                       <div className="servitut-redegoerelse__aktActions">
                                          {viewControl}
                                          <button
                                             type="button"
                                             className="servitut-redegoerelse__viewButton"
                                             disabled={!datoLoebenrCopyText}
                                             title={Localization.getText("Copy identification")}
                                             aria-label={Localization.getText("Copy identification")}
                                             onClick={() => void copyTextToClipboard(datoLoebenrCopyText)}
                                          >
                                             <MIBIcon icon="copy" style={{ fontSize: 14 }} />
                                             <span>{Localization.getText("Copy identification")}</span>
                                          </button>
                                       </div>
                                    </div>
                                    {headingText ? (
                                       <div className="servitut-redegoerelse__aktHeadingLine">
                                          <span className="servitut-redegoerelse__aktHeading">{headingText}</span>
                                       </div>
                                    ) : null}
                                    {phase === "A" ? (
                                       <ServitutPaategningNotes dokumentId={String(row.electronicServitut?.dokumentId || row.servitut?.dokumentId || "")} />
                                    ) : null}
                                 </div>
                                 {phase === "A" ? (
                                    <ServitutMolbakHelpButton
                                       context={servitutHelpContextFromMatrixRow(row, props.payload.matrix.columnDetails)}
                                    />
                                 ) : null}
                              </div>
                           </td>
                           <td className="servitut-redegoerelse__savedCell">
                              {awaitingAkt ? (
                                 <span className="servitut-redegoerelse__savedPending" title={Localization.getText("Fetching akt…")}>
                                    <Spinner animation="border" role="status" size="sm" />
                                    <span className="servitut-redegoerelse__savedPendingLabel">{Localization.getText("Fetching akt…")}</span>
                                 </span>
                              ) : phase === "B" ? (
                                 isStedfaestelseTab ? (
                                    <div className="servitut-redegoerelse__statusStack">
                                       {row.stedfaestelseAnnotation ? (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--saved">
                                             {Localization.getText("Saved")}
                                          </span>
                                       ) : (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                             {Localization.getText("Not saved yet")}
                                          </span>
                                       )}
                                    </div>
                                 ) : isSavingAnnotation || awaitingAi ? (
                                    <span className="servitut-redegoerelse__savedPending" title={isSavingAnnotation ? Localization.getText("Saving annotation…") : Localization.getText("Analyzing easement content...")}>
                                       {awaitingAi && !isSavingAnnotation ? <AiRobotIcon /> : null}
                                       <Spinner animation="border" role="status" size="sm" />
                                    </span>
                                 ) : (
                                    <div className="servitut-redegoerelse__statusStack">
                                       <ServitutAiFieldChip
                                          status={fieldStatus}
                                          text={
                                             fieldStatus === "not_run"
                                                && props.analysisField === "statement"
                                                && !hasCaseDescription
                                                ? Localization.getText("No problem statement")
                                                : undefined
                                          }
                                       />
                                       {fieldUntouchedAccepted ? (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--saved">
                                             {Localization.getText("Accepted")}
                                          </span>
                                       ) : (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                             {Localization.getText("Not accepted yet")}
                                          </span>
                                       )}
                                    </div>
                                 )
                              ) : (
                                 <div className="servitut-redegoerelse__statusStack">
                                    {actionStatus === "saving" ? (
                                       <span className="servitut-redegoerelse__savedPending" title={Localization.getText("Saving annotation…")}>
                                          <Spinner animation="border" role="status" size="sm" />
                                       </span>
                                    ) : isRowApproved(row) ? (
                                       <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--saved">
                                          {Localization.getText("Approved")}
                                       </span>
                                    ) : (
                                       <>
                                          {isImportedAnnotationPendingPageSearch(row) ? (
                                             <ServitutImportedChip />
                                          ) : (
                                             <>
                                                {isElectronicServitutRow(row) ? <ServitutDigitalDocumentChip /> : null}
                                                <ServitutProvenanceChip
                                                   provenance={getRowProvenance(row)}
                                                   hideAiGenerated
                                                   hideFetchedFromAkt={
                                                      documentExtent.kind === "entire_akt"
                                                      || isElectronicServitutRow(row)
                                                   }
                                                />
                                             </>
                                          )}
                                          <ServitutDocumentExtentChip extent={documentExtent} />
                                          <ServitutPageFindChip source={pageSourceForChip} />
                                          {isRowRejected(row) ? (
                                             <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--ignored">
                                                {Localization.getText("Ignored")}
                                             </span>
                                          ) : (
                                             <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                                {Localization.getText("Not approved yet")}
                                             </span>
                                          )}
                                       </>
                                    )}
                                 </div>
                              )}
                           </td>
                           {phase !== "B" ? (
                              <td className="servitut-redegoerelse__actionsCell">
                                 <div className="servitut-redegoerelse__pageActions">
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       disabled={
                                          props.canWriteA === false
                                          || isSavingAnnotation
                                          || awaitingAi
                                          || awaitingAkt
                                          || !canLocateSingleRow(row)
                                       }
                                       title={
                                          awaitingAkt
                                             ? Localization.getText("Fetching akt…")
                                             : !canLocateSingleRow(row)
                                                ? Localization.getText("No PDF available for this document")
                                                : Localization.getText("Run AI on this row")
                                       }
                                       onClick={() => props.onRunRowAi?.(row)}
                                    >
                                       {awaitingAi ? (
                                          <>
                                             <AiRobotIcon />
                                             <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                          </>
                                       ) : (
                                          <>
                                             <AiRobotIcon />
                                             <span>{Localization.getText("Run AI")}</span>
                                          </>
                                       )}
                                    </button>
                                    {isRowApproved(row) ? (
                                       canUndoRowPageApproval(row) ? (
                                          <button
                                             type="button"
                                             className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                             disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi}
                                             title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                             onClick={() => props.onRevertPages?.(row)}
                                          >
                                             {Localization.getText("Undo approve")}
                                          </button>
                                       ) : null
                                    ) : (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--approve"
                                          disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi || !getRowPageApprovalPlan(row).canApprove}
                                          title={
                                             awaitingAi
                                                ? Localization.getText("Cannot save while AI is running")
                                                : !isRowFetched(row)
                                                   ? Localization.getText("No PDF available for this document")
                                                   : !getRowPageApprovalPlan(row).canApprove
                                                      ? Localization.getText("Choose pages in the document first")
                                                      : undefined
                                          }
                                          onClick={() => props.onApprovePages?.(row)}
                                       >
                                          {Localization.getText("Approve")}
                                       </button>
                                    )}
                                    {isRowRejected(row) ? (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                          disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi}
                                          title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                          onClick={() => props.onRevertPages?.(row)}
                                       >
                                          {Localization.getText("Undo ignore")}
                                       </button>
                                    ) : (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--ignore"
                                          disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi}
                                          title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                          onClick={() => props.onRejectPages?.(row)}
                                       >
                                          {Localization.getText("Ignore")}
                                       </button>
                                    )}
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       disabled={
                                          props.canWriteA === false
                                          || isSavingAnnotation
                                          || awaitingAi
                                          || props.exporting
                                       }
                                       title={Localization.getText("Upload PDF")}
                                       onClick={() => {
                                          uploadRowRef.current = row;
                                          uploadInputRef.current?.click();
                                       }}
                                    >
                                       <MIBIcon icon="Import" style={{ fontSize: 14 }} />
                                       <span>{Localization.getText("Upload PDF")}</span>
                                    </button>
                                 </div>
                              </td>
                           ) : null}
                           {showAnalysisColumns ? (
                              <td className="servitut-redegoerelse__fieldCell servitut-redegoerelse__focusCol">
                                 {isStedfaestelseTab ? (
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       onClick={() => props.onOpenStedfaestelse?.(row)}
                                    >
                                       {Localization.getText("Locate on map")}
                                    </button>
                                 ) : (
                                    <div className="servitut-redegoerelse__textWithCopy">
                                       <div className="servitut-redegoerelse__fieldEditor">
                                          {props.analysisField === "entitledParty"
                                             && (canResolvePaataleberettiget(row) || getRowPaataleberettigetResolution(row)) ? (
                                             <div className="servitut-redegoerelse__paataleActions">
                                                {canResolvePaataleberettiget(row) ? (
                                                   <TooltipWrapper
                                                      title={
                                                         props.resolvingPaataleKeys.has(row.key)
                                                            ? Localization.getText("Resolving current entitled party…")
                                                            : Localization.getText("Find current entitled party")
                                                      }
                                                   >
                                                      <button
                                                         type="button"
                                                         className="servitut-redegoerelse__paataleResolve"
                                                         disabled={props.resolvingPaataleKeys.has(row.key) || awaitingAi || props.exporting}
                                                         onClick={() => props.onResolvePaataleberettiget(row)}
                                                         aria-label={Localization.getText("Find current entitled party")}
                                                      >
                                                         {props.resolvingPaataleKeys.has(row.key) ? (
                                                            <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                                         ) : (
                                                            <MIBIcon icon="switchLicense" style={{ fontSize: 14 }} />
                                                         )}
                                                      </button>
                                                   </TooltipWrapper>
                                                ) : null}
                                                {getRowPaataleberettigetResolution(row) ? (
                                                   <TooltipWrapper title={Localization.getText("Show suggestion details")}>
                                                      <button
                                                         type="button"
                                                         className="servitut-redegoerelse__paataleResolve"
                                                         onClick={() => setPaataleNoticeKey(row.key)}
                                                         aria-label={Localization.getText("Show suggestion details")}
                                                      >
                                                         <MIBIcon icon="mapLink" style={{ fontSize: 14 }} />
                                                      </button>
                                                   </TooltipWrapper>
                                                ) : null}
                                             </div>
                                          ) : null}
                                          {editingFieldKey === fieldDraftKey(row.key) ? (
                                             <textarea
                                                className="servitut-redegoerelse__fieldInput"
                                                value={fieldText}
                                                autoFocus
                                                disabled={props.canWriteB === false || isSavingAnnotation || props.exporting || awaitingAi}
                                                placeholder={Localization.getText("Write or correct this field")}
                                                rows={4}
                                                onChange={(event) => {
                                                   const next = event.currentTarget.value;
                                                   setFieldDrafts((prev) => ({ ...prev, [fieldDraftKey(row.key)]: next }));
                                                }}
                                                onBlur={(event) => {
                                                   const related = event.relatedTarget as HTMLElement | null;
                                                   if (related?.closest(".servitut-redegoerelse__fieldCell, .servitut-redegoerelse__pageActions")) {
                                                      return;
                                                   }
                                                   stopEditingField(row.key);
                                                }}
                                             />
                                          ) : (
                                             <div
                                                className={`servitut-redegoerelse__fieldPlain${fieldText.trim() ? "" : " servitut-redegoerelse__fieldPlain--empty"}`}
                                             >
                                                {fieldText.trim()}
                                             </div>
                                          )}
                                       </div>
                                       <div className="servitut-redegoerelse__fieldSideActions">
                                          <button
                                             type="button"
                                             className="servitut-redegoerelse__copyButton"
                                             disabled={!fieldText.trim()}
                                             title={Localization.getText("Copy to clipboard")}
                                             aria-label={Localization.getText("Copy to clipboard")}
                                             onClick={() => void copyTextToClipboard(fieldText)}
                                          >
                                             <MIBIcon icon="copy" style={{ fontSize: 14 }} />
                                          </button>
                                          {props.canWriteB !== false ? (
                                             editingFieldKey === fieldDraftKey(row.key) ? (
                                                <button
                                                   type="button"
                                                   className="servitut-redegoerelse__copyButton"
                                                   disabled={isSavingAnnotation || props.exporting || awaitingAi}
                                                   title={Localization.getText("Annuller")}
                                                   aria-label={Localization.getText("Annuller")}
                                                   onClick={() => {
                                                      clearFieldDraft(row.key);
                                                      stopEditingField(row.key);
                                                   }}
                                                >
                                                   <MIBIcon icon="close" style={{ fontSize: 14 }} />
                                                </button>
                                             ) : (
                                                <button
                                                   type="button"
                                                   className="servitut-redegoerelse__copyButton"
                                                   disabled={isSavingAnnotation || props.exporting || awaitingAi}
                                                   title={Localization.getText("Edit")}
                                                   aria-label={Localization.getText("Edit")}
                                                   onClick={() => setEditingFieldKey(fieldDraftKey(row.key))}
                                                >
                                                   <MIBIcon icon="edit" style={{ fontSize: 14 }} />
                                                </button>
                                             )
                                          ) : null}
                                       </div>
                                    </div>
                                 )}
                              </td>
                           ) : null}
                           {showAnalysisColumns && !isStedfaestelseTab ? (
                              <td className="servitut-redegoerelse__actionsCell">
                                 <div className="servitut-redegoerelse__pageActions">
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       disabled={
                                          props.canWriteB === false
                                          || awaitingAi
                                          || awaitingAkt
                                          || !isRowFetched(row)
                                       }
                                       title={
                                          awaitingAkt
                                             ? Localization.getText("Fetching akt…")
                                             : !isRowFetched(row)
                                                ? Localization.getText("No PDF available for this document")
                                                : Localization.getText("Run AI on this row")
                                       }
                                       onClick={() => props.onRunRowAi?.(row)}
                                    >
                                       {awaitingAi ? (
                                          <>
                                             <AiRobotIcon />
                                             <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                          </>
                                       ) : (
                                          <>
                                             <AiRobotIcon />
                                             <span>{Localization.getText("Run AI")}</span>
                                          </>
                                       )}
                                    </button>
                                    {fieldUntouchedAccepted ? (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                          disabled={props.canWriteB === false || isSavingAnnotation || props.exporting}
                                          onClick={() => {
                                             clearFieldDraft(row.key);
                                             stopEditingField(row.key);
                                             props.onUndoField(row);
                                          }}
                                       >
                                          {Localization.getText("Undo accept")}
                                       </button>
                                    ) : (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--accept"
                                          disabled={
                                             props.canWriteB === false
                                             || isSavingAnnotation
                                             || props.exporting
                                             || awaitingAi
                                             || !fieldText.trim()
                                          }
                                          title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                          onClick={() => {
                                             const next = fieldText.trim();
                                             if (!next) {
                                                return;
                                             }
                                             void Promise.resolve(props.onAcceptField(row, next)).then(() => {
                                                clearFieldDraft(row.key);
                                                stopEditingField(row.key);
                                             });
                                          }}
                                       >
                                          {Localization.getText("Accept field")}
                                       </button>
                                    )}
                                    {canResetFieldToSavedAnnotation(row, props.analysisField, hasFieldDraft(row.key)) ? (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--reset"
                                          disabled={props.canWriteB === false || isSavingAnnotation || props.exporting || awaitingAi}
                                          title={Localization.getText("Reset to saved annotation")}
                                          onClick={() => {
                                             clearFieldDraft(row.key);
                                             stopEditingField(row.key);
                                             props.onResetField?.(row);
                                          }}
                                       >
                                          {Localization.getText("Reset field")}
                                       </button>
                                    ) : null}
                                 </div>
                              </td>
                           ) : null}
                           {props.payload.matrix.columns.map((bfe) => (
                              <td key={`${row.key}-${bfe}`} className="servitut-redegoerelse__bfeCell">
                                 {row.includedBfes.includes(bfe) ? <span className="servitut-redegoerelse__included">✓</span> : ""}
                              </td>
                           ))}
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            {filteredRows.length === 0 ? (
               <div className="servitut-redegoerelse__empty">
                  {phase === "B" && !props.payload.matrix.rows.some(isRowApproved)
                     ? Localization.getText("No approved easements. Approve pages in Find and control first.")
                     : Localization.getText("No rows match the filter.")}
               </div>
            ) : null}
         </div>
         {paataleNoticeResolution ? (
            <AdvancedDragModal
               variant="Small"
               title={Localization.getText("AI suggested current")}
               onClose={() => setPaataleNoticeKey(null)}
               PosDefault={{ top: "120px", left: "50%", width: "560px", height: "620px", transform: "translate(-50%, 0)" }}
            >
               <ADModalBody>
                  <PaataleberettigetResolutionView
                     resolution={paataleNoticeResolution}
                     defaultOpenChain
                  />
               </ADModalBody>
            </AdvancedDragModal>
         ) : null}
      </div>
   );
}

function getServitutMatrixColumnLabel(payload: ServitutRedegoerelsePayload, bfe: number): string {
   return payload.matrix.columnLabels?.[bfe] ?? payload.matrix.columnLabels?.[String(bfe)] ?? `${bfe}`;
}

function getServitutMatrixColumnLabelParts(label: string): { matrikel: string; ejerlav?: string } {
   const [matrikel, ...ejerlavParts] = label.split(", ");
   return {
      matrikel: matrikel || label,
      ejerlav: ejerlavParts.join(", ") || undefined,
   };
}

function getServitutMatrixColumnHeaderShort(payload: ServitutRedegoerelsePayload, bfe: number): string {
   const detail = payload.matrix.columnDetails?.[String(bfe)];
   if (detail?.matrikler?.length) {
      return `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return parts.matrikel || `${bfe}`;
}

function getServitutMatrixColumnPopoverDetail(
   payload: ServitutRedegoerelsePayload,
   bfe: number
): ServitutMatrixColumnDetail {
   const fromPayload = payload.matrix.columnDetails?.[String(bfe)];
   if (fromPayload?.matrikler?.length) {
      return fromPayload;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return {
      matrikler: parts.matrikel ? [parts.matrikel] : [`${bfe}`],
      ejerlav: parts.ejerlav,
   };
}
function ServitutDatoLoebenrLink(props: {
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number];
}) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const label = props.row.identityLabel || props.row.datoLoebenr || props.row.displayAktId;
   const servitut = props.row.servitut;
   const canOpenDetails = Boolean(servitut && hasAccessToFeature(Feature.ServitutDetaljer));
   if (!canOpenDetails) {
      return <div className="servitut-redegoerelse__aktId">{label}</div>;
   }
   return (
      <a
         className="servitut-redegoerelse__aktId servitut-redegoerelse__aktId--link"
         href=""
         title={Localization.getText("Servitutdetails")}
         onClick={(event) => {
            event.preventDefault();
            windowDispatch(openWindow(WindowId.ServitutDetails, buildServitutDetailsParams(servitut)));
         }}
      >
         {label}
      </a>
   );
}

function isServitutRowAwaitingAi(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   aiJobKeys: Set<string>,
): boolean {
   return aiJobKeys.has(row.key);
}
function hasContentAnalysisFields(item?: LLMAnalysisResultItem): boolean {
   return Boolean(
      item?.summary
      || item?.servitut_type
      || item?.paataleberettiget
      || item?.case_relevance_reasoning
      || item?.case_relevant === true,
   );
}
function getRowPaataleberettigetResolution(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
) {
   return row.analysisItem?.paataleberettiget_resolution
      || getSavedPaataleberettigetResolution(row.fieldAnnotations?.entitledParty);
}

function canResolvePaataleberettiget(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   return isResolvableHistoricalPaatale(
      row.analysisItem?.paataleberettiget
      || row.analysisItem?.paataleberettiget_resolution?.historical_name
      || getSavedPaataleberettigetResolution(row.fieldAnnotations?.entitledParty)?.historical_name,
   );
}

function stamOplysningerHintForRow(
   payload: ServitutRedegoerelsePayload,
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
) {
   for (const bfe of row.includedBfes || []) {
      const hint = payload.aktSearchByBfe?.[String(bfe)];
      if (hint) {
         return hint;
      }
   }
   return undefined;
}

function isRowAwaitingAkt(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   return row.job === "fetching";
}

function canLocateSingleRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (row.documentSource === "rigsarkivetRequired") {
      return false;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage" && !row.fileOrigin) {
      return false;
   }
   return isRowFetched(row) || Boolean(row.aktId) || row.documentSource === "customUpload";
}

function formatServitutType(value?: string): string {
   if (!value) return "-";
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") return Localization.getText("Easement of use");
   if (normalized === "tilstandsservitut") return Localization.getText("Condition easement");
   return value;
}

type RowActionStatus = "saving" | "needsAnalysis" | "notSaved" | "saved";

function getRowActionStatus(params: {
   hasAnalysis: boolean;
   isSaving: boolean;
   wasJustSaved: boolean;
   hasUnsavedChange: boolean;
   hasSavedAnalysis: boolean;
}): RowActionStatus {
   if (params.isSaving) return "saving";
   if (!params.hasAnalysis) return "needsAnalysis";
   if (params.wasJustSaved) return "saved";
   if (params.hasUnsavedChange) return "notSaved";
   if (params.hasSavedAnalysis) return "saved";
   return "notSaved";
}

function normalizeConfidenceValue(value?: number): number | null {
   if (value === undefined || value === null) {
      return null;
   }
   if (!Number.isFinite(value)) {
      return null;
   }
   // Older cached/dev results used 1-10. Normalize defensively while backend now returns 0-1.
   return value > 1 ? value / 10 : value;
}

function sortServitutRows<T extends { includedBfes: number[]; priority: string; datoLoebenr?: string; displayAktId: string }>(
   rows: T[],
   columns: number[],
   datoSort: "default" | "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      if (datoSort !== "default") {
         const dateDiff = compareDatoLoebenr(a, b);
         return datoSort === "asc" ? dateDiff : -dateDiff;
      }

      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function sortRowsByReviewConfidence<T extends ServitutRedegoerelsePayload["matrix"]["rows"][number]>(
   rows: T[],
   direction: "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      const diff = getReviewConfidenceSortValue(a) - getReviewConfidenceSortValue(b);
      return direction === "asc" ? diff : -diff;
   });
}

type ServitutMatrixSortColumn =
   | "dato"
   | "confidence"
   | "priority"
   | "servitutType"
   | "entitledParty"
   | "caseRelevant";

export type ServitutMatrixSortState =
   | { column: ServitutMatrixSortColumn; direction: "asc" | "desc" }
   | null;

function getCaseRelevantSortRank(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   hasCaseDescription: boolean,
): number {
   if (!hasCaseDescription) return 0;
   const v = row.analysisItem?.case_relevant;
   if (v === true) return 3;
   if (v === false) return 2;
   return 1;
}

function sortServitutRowsByPriority(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const pa = parseInt(a.priority, 10);
      const pb = parseInt(b.priority, 10);
      const na = Number.isFinite(pa) ? pa : Number.MAX_SAFE_INTEGER;
      const nb = Number.isFinite(pb) ? pb : Number.MAX_SAFE_INTEGER;
      const diff = na - nb;
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByServitutType(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = formatServitutType(a.analysisItem?.servitut_type);
      const sb = formatServitutType(b.analysisItem?.servitut_type);
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByEntitledParty(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = (a.analysisItem?.paataleberettiget || "").trim();
      const sb = (b.analysisItem?.paataleberettiget || "").trim();
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByCaseRelevant(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const diff = getCaseRelevantSortRank(a, hasCaseDescription) - getCaseRelevantSortRank(b, hasCaseDescription);
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function applyServitutMatrixSort(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   sortState: ServitutMatrixSortState,
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   if (!sortState) {
      return sortServitutRows(rows, columns, "default");
   }
   const { column, direction } = sortState;
   const baseDefault = sortServitutRows(rows, columns, "default");

   switch (column) {
      case "dato":
         return sortServitutRows(rows, columns, direction === "asc" ? "asc" : "desc");
      case "confidence":
         return sortRowsByReviewConfidence(baseDefault, direction);
      case "priority":
         return sortServitutRowsByPriority(rows, columns, direction);
      case "servitutType":
         return sortServitutRowsByServitutType(rows, columns, direction);
      case "entitledParty":
         return sortServitutRowsByEntitledParty(rows, columns, direction);
      case "caseRelevant":
         return sortServitutRowsByCaseRelevant(rows, columns, hasCaseDescription, direction);
      default:
         return baseDefault;
   }
}

function getReviewConfidenceSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const item = row.analysisItem;
   if (!item) {
      return -1;
   }
   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   if (item.needs_review === true) {
      return -0.5 + ((confidence ?? 0) / 100);
   }
   return confidence ?? -0.25;
}

function getFirstBfeColumnIndex(includedBfes: number[], columns: number[]): number {
   const columnIndexes = includedBfes.map((bfe) => {
      const index = columns.indexOf(bfe);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
   });
   return Math.min(...columnIndexes);
}

function comparePriorityThenDato(
   a: { priority: string; datoLoebenr?: string; displayAktId: string },
   b: { priority: string; datoLoebenr?: string; displayAktId: string }
): number {
   const priorityDiff = (parseInt(a.priority) || 0) - (parseInt(b.priority) || 0);
   if (priorityDiff !== 0) return priorityDiff;
   return compareDatoLoebenr(a, b);
}

function compareDatoLoebenr(
   a: { datoLoebenr?: string; displayAktId: string },
   b: { datoLoebenr?: string; displayAktId: string }
): number {
   const aDate = parseDatoLoebenrDate(a.datoLoebenr || a.displayAktId);
   const bDate = parseDatoLoebenrDate(b.datoLoebenr || b.displayAktId);
   if (aDate !== bDate) return aDate - bDate;
   return (a.datoLoebenr || a.displayAktId).localeCompare(b.datoLoebenr || b.displayAktId);
}

function parseDatoLoebenrDate(value: string): number {
   const trimmed = value.trim();
   const danishDate = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed);
   if (danishDate) {
      return toValidUtcDate(Number(danishDate[3]), Number(danishDate[2]), Number(danishDate[1]));
   }
   const compactDanishDate = /^(\d{2})(\d{2})(\d{4})/.exec(trimmed);
   if (compactDanishDate) {
      const date = toValidUtcDate(Number(compactDanishDate[3]), Number(compactDanishDate[2]), Number(compactDanishDate[1]));
      if (date !== Number.MAX_SAFE_INTEGER) return date;
   }
   const compactIsoDate = /^(\d{4})(\d{2})(\d{2})/.exec(trimmed);
   if (compactIsoDate) {
      return toValidUtcDate(Number(compactIsoDate[1]), Number(compactIsoDate[2]), Number(compactIsoDate[3]));
   }
   return Number.MAX_SAFE_INTEGER;
}

function toValidUtcDate(year: number, month: number, day: number): number {
   if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) {
      return Number.MAX_SAFE_INTEGER;
   }
   return Date.UTC(year, month - 1, day);
}
